import { useCallback, useEffect, useRef, useState } from 'react'
import {
  addPreset,
  confirmMeal,
  fetchMeals,
  fetchPresets,
  removeMeal,
  scaleMeal,
  analyzePhoto,
  type FoodPreset,
  type MealRow,
} from '../lib/api'
import { Button, Empty, ErrorBanner, Group, LoadError, Row, Screen, Spinner } from '../components/ui'
import { IconCamera, IconPlus } from '../components/icons'

const KIND_LABELS: Record<string, string> = {
  breakfast: 'Завтрак',
  lunch: 'Обед',
  snack: 'Перекус',
  dinner: 'Ужин',
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
}

export function FoodScreen() {
  const [meals, setMeals] = useState<MealRow[] | null>(null)
  const [presets, setPresets] = useState<FoodPreset[]>([])
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [openMeal, setOpenMeal] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)
  const [analyzing, setAnalyzing] = useState(false)
  const [justAdded, setJustAdded] = useState<{ mealId: string; name: string } | null>(null)
  const cameraInput = useRef<HTMLInputElement>(null)

  const reload = useCallback(async () => {
    try {
      const [mealsData, presetsData] = await Promise.all([fetchMeals(), fetchPresets()])
      setMeals(mealsData.meals)
      setPresets(presetsData.presets)
      setError(null)
    } catch (cause) {
      setError((cause as Error).message)
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  async function act(mealId: string, action: () => Promise<unknown>) {
    setBusy(mealId)
    try {
      await action()
      await reload()
    } catch (cause) {
      setError((cause as Error).message)
    } finally {
      setBusy(null)
    }
  }

  async function analyze(file: File) {
    setAnalyzing(true)
    setError(null)
    try {
      const reader = new FileReader()
      const base64: string = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '')
        reader.onerror = () => reject(new Error('Не удалось прочитать файл'))
        reader.readAsDataURL(file)
      })
      await analyzePhoto(base64, file.type || 'image/jpeg')
      await reload()
    } catch (cause) {
      setError((cause as Error).message)
    } finally {
      setAnalyzing(false)
      if (cameraInput.current) cameraInput.current.value = ''
    }
  }

  if (error && !meals) return <LoadError message={error} onRetry={() => void reload()} />
  if (!meals) return <Spinner label="Загружаю дневник…" />

  const total = meals.reduce((sum, meal) => sum + meal.total_kcal, 0)
  const protein = meals.reduce((sum, meal) => sum + Number(meal.protein_g ?? 0), 0)
  const unchecked = meals.filter((meal) => meal.status === 'draft').length

  return (
    <Screen title="Еда">
      {error && <ErrorBanner message={error} onClose={() => setError(null)} />}

      {/* Живая камера в WebView на iOS сломана — только выбор файла с
          подсказкой камере (правило 7 в CLAUDE.md проекта). */}
      <input
        ref={cameraInput}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0]
          if (file) void analyze(file)
        }}
      />
      <Button full variant="tinted" disabled={analyzing} onClick={() => cameraInput.current?.click()}>
        <IconCamera size={18} />
        {analyzing ? 'Считаю…' : 'Снять еду'}
      </Button>

      <div className="flex gap-8 px-1">
        <Total label="Калории" value={total} />
        <Total label="Белок" value={Math.round(protein)} unit="г" />
        <Total label="Приёмов" value={meals.length} />
      </div>

      <Group
        caption="Сегодня"
        footnote={
          unchecked > 0
            ? `${unchecked} ${unchecked === 1 ? 'запись посчитана' : 'записи посчитаны'} мной и уже входит в итог. Открой и проверь.`
            : undefined
        }
      >
        {meals.length === 0 && <Empty>Пока пусто. Сними еду кнопкой сверху или пришли фото боту.</Empty>}

        {meals.map((meal) => (
          <div key={meal.id}>
            <Row
              title={
                <span className="flex items-center gap-2">
                  {KIND_LABELS[meal.kind ?? ''] ?? 'Приём пищи'}
                  {meal.status === 'draft' && (
                    <span className="rounded-full bg-sys-orange/20 px-2 py-0.5 text-[11px] text-sys-orange">
                      не проверено
                    </span>
                  )}
                </span>
              }
              subtitle={`${formatTime(meal.eaten_at)} · ${meal.meal_items.map((item) => item.name).join(', ')}`}
              value={`${meal.total_kcal}`}
              onPress={() => setOpenMeal((current) => (current === meal.id ? null : meal.id))}
            />

            {openMeal === meal.id && (
              <div className="bg-ink-surface2/60 px-4 pb-4 pt-1">
                <ul className="mb-3">
                  {meal.meal_items.map((item) => (
                    <li key={item.id} className="flex justify-between py-1 text-[15px]">
                      <span className="text-ink-2">
                        {item.name}
                        {item.grams ? ` · ${Math.round(item.grams)} г` : ''}
                      </span>
                      <span className="numerals">{item.kcal}</span>
                    </li>
                  ))}
                </ul>

                <div className="numerals mb-3 text-[13px] text-ink-2">
                  Б {Math.round(Number(meal.protein_g ?? 0))} · Ж{' '}
                  {Math.round(Number(meal.fat_g ?? 0))} · У {Math.round(Number(meal.carb_g ?? 0))}
                  {meal.kcal_low && meal.kcal_high && (
                    <span className="ml-2">
                      оценка {meal.kcal_low}–{meal.kcal_high} ккал
                    </span>
                  )}
                </div>

                {meal.ai_assumptions.length > 0 && (
                  <p className="footnote mb-3">Предположил: {meal.ai_assumptions.join('; ')}</p>
                )}

                <div className="flex flex-wrap gap-2">
                  {meal.status === 'draft' && (
                    <Button
                      variant="filled"
                      color="var(--color-sys-green)"
                      disabled={busy === meal.id}
                      onClick={() => act(meal.id, () => confirmMeal(meal.id))}
                    >
                      Верно
                    </Button>
                  )}
                  <Button
                    disabled={busy === meal.id}
                    onClick={() => act(meal.id, () => scaleMeal(meal.id, 0.5))}
                  >
                    Половина
                  </Button>
                  <Button
                    disabled={busy === meal.id}
                    onClick={() => act(meal.id, () => scaleMeal(meal.id, 1.5))}
                  >
                    Полторы
                  </Button>

                  {/* Удаление в два шага: соседняя кнопка правит порцию, и
                      промах не должен стирать приём пищи. */}
                  {confirmDelete === meal.id ? (
                    <>
                      <Button
                        variant="filled"
                        color="var(--color-sys-red)"
                        disabled={busy === meal.id}
                        onClick={() => {
                          setConfirmDelete(null)
                          void act(meal.id, () => removeMeal(meal.id))
                        }}
                      >
                        Удалить
                      </Button>
                      <Button variant="plain" onClick={() => setConfirmDelete(null)}>
                        Отмена
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="plain"
                      color="var(--color-sys-red)"
                      disabled={busy === meal.id}
                      onClick={() => setConfirmDelete(meal.id)}
                    >
                      Удалить
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </Group>

      {justAdded && (
        <div className="flex items-center gap-3 rounded-card bg-ink-surface px-4 py-3 text-[15px]">
          <span className="min-w-0 flex-1">Записал: {justAdded.name}</span>
          <Button
            variant="plain"
            onClick={() => {
              const target = justAdded.mealId
              setJustAdded(null)
              void act(target, () => removeMeal(target))
            }}
          >
            Отменить
          </Button>
        </div>
      )}

      <Group caption="Напитки" footnote="По фото напитки распознаются плохо — тапни строку, чтобы записать.">
        {presets
          .filter((preset) => preset.is_drink)
          .map((preset) => (
            <Row
              key={preset.id}
              title={preset.name}
              value={`${preset.kcal} ккал`}
              onPress={() =>
                act(preset.id, async () => {
                  const { mealId } = await addPreset(preset.id)
                  setJustAdded({ mealId, name: preset.name })
                })
              }
              accessory={<IconPlus size={18} className="shrink-0 text-ink-2" />}
            />
          ))}
      </Group>
    </Screen>
  )
}

function Total({ label, value, unit }: { label: string; value: number; unit?: string }) {
  return (
    <div>
      <div className="footnote">{label}</div>
      <div className="numerals text-[28px] font-semibold leading-tight">
        {value}
        {unit && <span className="ml-1 text-[15px] font-normal text-ink-2">{unit}</span>}
      </div>
    </div>
  )
}
